import type {
  AdvancedFormulaResponse,
  BundleResponse,
  CreateBundleResponse,
  ErrorResponse,
  GenresResponse,
  InfoGuideResponse,
  Instrument,
  FormulaExtraParamsResponse,
  SongStructuresResponse,
  TextFormulaResponse,
  TextToMusicPlaceholderResponse,
  VegaSongResponse,
  FormulaResponse,
  BundlesResponse,
} from 'services/api/responses/responseTypes';
import {
  BundleType,
  type Formula,
  type FormulaField,
  type FormulaFieldValue,
  FormulaFields,
  type Instruments,
  type Key,
  KeyQuality,
  KeyRoot,
  type VegaBundle,
  type VegaSong,
  type BundleSongState,
  BundleSongStatus,
  BundlesPage,
} from 'services/store/ai/types';
import messages from 'services/locales/en/messages';
import { DEFAULT_ENTROPY } from 'pages/Remixer/constants';
import type { UserLoop } from 'types';
import { IGenre, SongType } from '../entities';

const SIMPLE_FORMULA_FIELDS = <const>[
  FormulaFields.Duration,
  FormulaFields.Energy,
  FormulaFields.Bpm,
  FormulaFields.GenreBlend,
  FormulaFields.Structure,
];

type SimpleFormulaFields = (typeof SIMPLE_FORMULA_FIELDS)[number];

export const modifySuggestedInstruments = (
  recommendedInstruments: Instrument[]
) => {
  const result = {} as Record<Instrument, number>;

  recommendedInstruments.forEach((item) => {
    if (result[item]) {
      result[item] += 1;
      return;
    }

    result[item] = 1;
  });

  return result;
};

export const transformInfoGuideResponse = (
  response: Array<InfoGuideResponse>
) =>
  response.map((data) => ({
    index: data.index,
    title: data.title,
    description: data.description,
    secondaryDescription: data.secondary_description,
    primaryImage: data.primary_image,
    secondaryImage: data.secondary_image,
    altPrimary: data.alt_primary,
    altSecondary: data.alt_secondary,
    tip: data.tip,
  }));

export const transformGenresResponse = (
  genres: GenresResponse
): Array<IGenre> =>
  genres.map(
    ({
      image_url: imageUrl,
      template_names: templateNames,
      bpm: recommendedBpm,
      suggested_instruments: suggestedInstruments,
      micro_genres: microGenres,
      ...data
    }) => ({
      ...data,
      imageUrl,
      templateNames,
      recommendedBpm,
      recommendedInstruments: modifySuggestedInstruments(suggestedInstruments),
      microGenres: transformGenresResponse(microGenres),
    })
  );

export const transformSongStructuresResponse = (
  structures: SongStructuresResponse
) =>
  structures.map(({ image_url: imageUrl, ...data }) => ({
    ...data,
    imageUrl,
  }));

export const transformTextToMusicPlaceholderResponse = ({
  prompts,
}: TextToMusicPlaceholderResponse) => prompts;

export const transformToError = (response: ErrorResponse) => {
  const { data } = response;

  if (!data) {
    return { message: messages.error.unknown };
  }

  return { message: response.data.error_description };
};

export const transformUserLoop = (
  extraParams: FormulaExtraParamsResponse | null
): UserLoop | null => {
  if (!extraParams) {
    return null;
  }

  const { entropy, user_audio: userAudio } = extraParams;
  if (!userAudio) {
    return null;
  }

  const { id: loopId, filename: loopFileName } = userAudio;

  return {
    loopId,
    loopFileName,
    entropy: entropy ?? DEFAULT_ENTROPY,
    instrument: '',
  };
};

export const transformFormulaKey = (
  key: AdvancedFormulaResponse['key']
): Key['value'] | null => {
  if (!key) {
    return null;
  }
  const [root, quality] = key.split('-');
  const rootIsValid = Object.values<string>(KeyRoot).includes(root);
  const qualityIsValid = Object.values<string>(KeyQuality).includes(quality);

  const valueIsValid = qualityIsValid && rootIsValid;

  if (!valueIsValid) {
    return null;
  }

  return { quality: quality as KeyQuality, root: root as KeyRoot };
};

export const transformFormulaInstruments = (
  instruments: AdvancedFormulaResponse['instruments']
): Instruments['value'] => {
  if (instruments === null) {
    return {};
  }
  return instruments.reduce<Instruments['value']>((acc, instrument) => {
    const currentValue = acc[instrument] ?? 0;
    return {
      ...acc,
      [instrument]: currentValue + 1,
    };
  }, {});
};

export const transformFormulaField = (
  formula: FormulaResponse,
  field: FormulaFields
): FormulaFieldValue | null => {
  if (field === FormulaFields.UserLoop && formula.extra_params) {
    return transformUserLoop(formula.extra_params);
  }

  if (field === FormulaFields.Instruments && formula.instruments) {
    return transformFormulaInstruments(formula.instruments);
  }

  if (field === FormulaFields.Key && formula.key) {
    return transformFormulaKey(formula.key);
  }

  if (SIMPLE_FORMULA_FIELDS.includes(field as SimpleFormulaFields)) {
    return formula[field as SimpleFormulaFields] ?? null;
  }

  return null;
};

export const transformFormulaResponse = (
  formula: AdvancedFormulaResponse | TextFormulaResponse
): Formula => {
  const fields = Object.values(FormulaFields);

  const formulaFields = fields.reduce<Formula>((acc, field) => {
    const value = transformFormulaField(formula, field);

    if (value === null) {
      return acc;
    }

    return [
      ...acc,
      {
        field,
        value,
      } as FormulaField,
    ];
  }, []);

  return formulaFields;
};

export const tranfsormVegaSongResponse = (song: VegaSongResponse): VegaSong => {
  const {
    music_file_path: musicFilePath,
    xml_file_path: xmlFilePath,
    wav_file_path: wavFilePath,
    created_at: createdAt,
    is_in_library: isInLibrary,
    is_preview: isPreview,
    formula,
    ...data
  } = song;

  return {
    ...data,
    songType: SongType.AI_GENERATED,
    genre: formula.genre,
    musicFilePath,
    xmlFilePath,
    wavFilePath,
    createdAt,
    isInLibrary,
    isPreview,
    formula: transformFormulaResponse(formula),
  };
};

export const tranfsormVegaSongsResponse = ({
  vega_songs: songs,
  capacity,
  is_finished: isFinished,
}: BundleResponse | CreateBundleResponse): BundleSongState[] =>
  Array.from({ length: capacity }).reduce<BundleSongState[]>(
    (acc, _item, index) => {
      const songResponse = songs[index];

      if (isFinished && !songResponse) {
        return acc;
      }

      if (!songResponse) {
        return [
          ...acc,
          {
            status: BundleSongStatus.LOADING,
          },
        ];
      }

      return [
        ...acc,
        {
          status: BundleSongStatus.SUCCESS,
          song: tranfsormVegaSongResponse(songResponse),
        },
      ];
    },
    []
  );

export const transformBundleResponse = (
  response: BundleResponse | CreateBundleResponse
): VegaBundle => {
  const {
    id,
    capacity,
    created_at: createdAt,
    is_finished: isFinished,
    is_preview: isPreview,
    type: bundleType,
    formula: formulaResponse,
  } = response;

  const formula = transformFormulaResponse(formulaResponse);

  const transformedData = {
    id,
    genre: formulaResponse.genre,
    capacity,
    bundleType,
    isPreview,
    createdAt,
    createdAtTimestamp: +new Date(createdAt),
    isFinished,
    formula,
    songs: tranfsormVegaSongsResponse(response),
  };

  if (bundleType === BundleType.TEXT2MUSIC) {
    return {
      ...transformedData,
      bundleType: BundleType.TEXT2MUSIC,
      prompt: formulaResponse.prompt,
    };
  }

  return {
    ...transformedData,
    bundleType: BundleType.ADVANCED,
  };
};

export const transformBundlesResponse = ({
  items,
  pagination_data: {
    current_page: currentPage,
    last_page: lastPage,
    items_per_page: itemsPerPage,
    items_count: itemsCount,
    total_items: totalItems,
    total_pages: totalPages,
  },
}: BundlesResponse): BundlesPage => ({
  items: items.map(transformBundleResponse),
  pagination: {
    currentPage,
    lastPage,
    itemsPerPage,
    itemsCount,
    totalItems,
    totalPages,
  },
});
